import React from 'react';

import { Link } from 'react-router-dom';

import './css/Speed.css'

import frameSources from './callToActionFrames'
import { useCursorRimLight } from './rimLight'


// What the service is sized for, shown rather than said: a stream of photos
// drifting across the frame on four lanes at different depths, each one
// flashing as it crosses the beam and coming out smaller and marked, and
// never stopping. The figures live on the Performances page, and the tile
// beside the stream only sends the reader there.

// The wordmark, as drawn at the end of the logo reveal.
const LOGO: string = frameSources[ frameSources.length - 1 ]

// Small copies of real photographs (200px on the long side, see
// images/thumbs), so the stream is made of pictures and not of glyphs. Each
// lane takes its own slice of the list, so no picture shows twice at once.
interface Thumb {
	src: string
	bPortrait: boolean
}

const THUMBS: Thumb[] = [
	{ src: require( './images/thumbs/Photo.1.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.2.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.3.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.4.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.5.jpg' ), bPortrait: false },
	{ src: require( './images/thumbs/Photo.6.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.7.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.8.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.9.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.10.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.11.jpg' ), bPortrait: false },
	{ src: require( './images/thumbs/Photo.12.jpg' ), bPortrait: false },
	{ src: require( './images/thumbs/Photo.13.jpg' ), bPortrait: false },
	{ src: require( './images/thumbs/Photo.14.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.15.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.16.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.17.jpg' ), bPortrait: false },
	{ src: require( './images/thumbs/Photo.18.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.19.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.20.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.21.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.22.jpg' ), bPortrait: false },
	{ src: require( './images/thumbs/Photo.23.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.24.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.25.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.26.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.27.jpg' ), bPortrait: true },
	{ src: require( './images/thumbs/Photo.28.jpg' ), bPortrait: true },
]

// A lane is a depth: the nearer it is, the larger, brighter and quicker its
// pictures. Each lane runs at its own pace, so the stream never falls into
// step with itself, and its pictures are spread evenly along it.
interface Lane {
	///@brief   Vertical position in the frame.
	y: string
	///@brief   One crossing of the frame, in seconds.
	duration: number
	items: number
	///@brief   Long side of a picture, in CSS px.
	size: number
	opacity: number
	///@brief   One up-and-down drift, in seconds.
	bob: number
}

const LANES: Lane[] = [
	{ y: '26%', duration: 10.5, items: 5, size: 46, opacity: 0.5, bob: 4.3 },
	{ y: '48%', duration: 8.2, items: 5, size: 58, opacity: 0.78, bob: 3.5 },
	{ y: '71%', duration: 6.6, items: 5, size: 70, opacity: 1, bob: 3.0 },
	{ y: '90%', duration: 9.1, items: 4, size: 40, opacity: 0.6, bob: 3.9 },
]

///@brief   The picture for item `i` of lane `l`: lanes start at different
///         points of the list and step through it, so neighbours differ and
///         the same picture is never on two lanes at the same time.
function thumbFor( l: number, i: number ): Thumb {
	const perLane: number = Math.floor( THUMBS.length / LANES.length )
	return THUMBS[ ( l * perLane + i ) % THUMBS.length ]
}

///@brief   Short side over long side of a picture.
const ASPECT: number = 0.72


function Picture( { thumb, size }: { thumb: Thumb, size: number } ): React.JSX.Element {
	const w: number = thumb.bPortrait ? Math.round( size * ASPECT ) : size
	const h: number = thumb.bPortrait ? size : Math.round( size * ASPECT )

	return (
		<div className="Speed-Thumb" style={ { width: `${ w }px`, height: `${ h }px` } } >
			<img src={ thumb.src } alt="" loading="lazy" />
			<span className="Speed-Thumb-Mark" aria-hidden="true" />
			<span className="Speed-Thumb-Flash" aria-hidden="true" />
		</div>
	)
}


export function Speed(): React.JSX.Element {

	const gridRef = React.useRef<HTMLDivElement>( null )
	useCursorRimLight( gridRef, '.Frame, .Tile' )

	return (
		<div className="Pane-Light Speed" id="speed" >
			<div className="Delimiter" />
			<div className="Container" >

				<div className="Section-Head" data-fade >
					<div className="Section-Kicker" >Speed</div>
					<div className="Title text-shadow-2" >Built for very large volumes.</div>
				</div>

				<div className="Section-Grid" ref={ gridRef } >

					<div
						className="Frame Speed-Frame"
						role="img"
						aria-label="A continuous stream of photos crosses Underlayer's beam and comes out marked and resized."
						data-fade
					>
						<div className="Speed-Flow" >

							{ LANES.map( ( lane: Lane, l: number ): React.JSX.Element => (
								<div
									className="Speed-Lane"
									key={ l }
									style={ {
										'--y': lane.y,
										'--dur': `${ lane.duration }s`,
										'--bob': `${ lane.bob }s`,
										'--opacity': `${ lane.opacity }`,
									} as React.CSSProperties }
								>
									{ Array.from( { length: lane.items }, ( _: unknown, i: number ): React.JSX.Element => (
										<div
											className="Speed-Item"
											key={ i }
											style={ { '--delay': `${ -( i * lane.duration / lane.items ) }s` } as React.CSSProperties }
										>
											<div className="Speed-Bob" >
												{ /* Neighbours on a lane, and lanes next to each other,
												     get different pictures. */ }
												<Picture thumb={ thumbFor( l, i ) } size={ lane.size } />
											</div>
										</div>
									) ) }
								</div>
							) ) }

							<div className="Speed-Beam-Field" aria-hidden="true" />
							<div className="Speed-Beam" aria-hidden="true" />

							<div className="Speed-Badge" >
								<img className="Speed-Badge-Logo" src={ LOGO } alt="" />
							</div>
						</div>

						{ /* Outside the stream, so the fade at its edges leaves them whole. */ }
						<span className="Speed-Flow-Label Speed-Flow-Label-In" >your images</span>
						<span className="Speed-Flow-Label Speed-Flow-Label-Out" >marked, delivered</span>
					</div>

					<div className="Tile" data-fade data-fade-delay="0.1" >
						<div className="Tile-Title" >On the fly</div>
						<div className="Tile-Text" >
							Each image is resized, converted and marked the moment it is asked for.
						</div>
					</div>

					<Link className="Tile Tile-Link" to="/performances" data-fade data-fade-delay="0.15" >
						<span className="Tile-Kicker" >Performances</span>
						<span className="Tile-Title" >See the benchmark</span>
						<span className="Tile-Text Speed-Link-Text" >
							Faster than the fastest open-source image proxy, in all but one of the scenarios we ran.
						</span>
						<span className="Tile-Arrow" aria-hidden="true" >→</span>
					</Link>

				</div>
			</div>
		</div>
	)

}
